import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { mobileMediaQuery, useIsMobile } from '../../utils/responsive';
import { captureEvent } from '../../utils/analytics';
import { getFarmPath } from '../../utils/utils';
import ChevronIcon from '../icons/ChevronIcon';
import Content from '../layout/Content';
import ProjectViewsDropdown from '../ProjectViewsDropdown';
import { ShrinkContent } from '../ShrinkContent';
import LineItemsTable from '../tables/LineItemsTable';
import NewFarmButton from './NewFarmButton';
import { ControlledTabs } from '../basics/Tabs';

const CellMargins = styled.div`
  margin-left: var(--cell-padding);
  @media ${mobileMediaQuery} {
    margin-left: 0;
  }
`;

const LargeProjectsDropdown = styled(ProjectViewsDropdown)`
  padding: var(--cell-padding);
  font-size: 1.4em;
  ${ChevronIcon} {
    width: 1rem;
    height: 1rem;
  }
`;

const TABS = [
  { display: 'Active', value: 'Active' },
  { display: 'Archived', value: 'Archived' },
];

const FarmsTable = ({ header, farms, createButtonProps, ...props }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const { pathname: source } = useLocation();

  const [activeTab, setActiveTab] = useState(TABS[0].value);
  const hasArchivedFarms = useMemo(
    () => farms?.some((farm) => farm.isArchived),
    [farms],
  );

  // if a user unarchives the last archived farm
  // reset the active tab
  useEffect(() => {
    if (!hasArchivedFarms) {
      setActiveTab(TABS[0].value);
    }
  }, [hasArchivedFarms]);

  const getItems = (items) => {
    return items
      .filter((farm) =>
        activeTab === 'Active' ? !farm.isArchived : farm.isArchived,
      )
      .map((farm) => ({
        ...farm,
        onClick: (e) => {
          const inNewTab = e.metaKey || e.ctrlKey;
          captureEvent('farm-opened', { location: 'project table', inNewTab });
          e.stopPropagation();

          const path = getFarmPath(farm.id, isMobile ? 'map' : 'fields');
          if (inNewTab) {
            window.open(path, '_blank');
          } else {
            navigate(path);
          }
        },
        source,
      }));
  };

  const { className } = createButtonProps || {};

  return (
    <Content noPadding={isMobile}>
      <CellMargins>
        {isMobile ? (
          <LargeProjectsDropdown
            color="light"
            selectedOption={header}
            className="mt-1"
          />
        ) : (
          <h1 className="mt-5 mb-4">{header}</h1>
        )}
        {createButtonProps && (
          <NewFarmButton
            {...createButtonProps}
            className={`mt-1 mb-3 ${className}`}
          />
        )}
      </CellMargins>
      <ShrinkContent>
        <LineItemsTable
          data={farms}
          getItems={getItems}
          hover
          tableHeader={
            hasArchivedFarms ? (
              <ControlledTabs
                tabs={TABS}
                onTabChange={setActiveTab}
                activeTab={activeTab}
              />
            ) : undefined
          }
          {...props}
        />
      </ShrinkContent>
    </Content>
  );
};

export default FarmsTable;
